import { ref, computed } from "vue";
import api from "~/api";
import type { Face } from "~/types";
import appConfig from "~/app.config";

export function useFaces() {
  const faces = ref<Face[]>([]);
  const loading = ref(false);
  const selectedFile = ref<File | null>(null);
  const snackbar = useSnackbarStore();
  const globalLoading = useLoadingStore();
  const isDragging = ref(false);
  const utils = useUtils();

  const { $logError, $logInfo } = useNuxtApp();

  const comm = useCommStore();
  const isEmpty = computed(() => faces.value.length <= 0);

  const loadFaces = async () => {
    try {
      loading.value = true;
      const faceList = await api.getFaceList(1, 1000);
      faces.value = faceList || [];
    } catch (error) {
      snackbar.showError(error.response?.data?.message || "Unknown error");
    } finally {
      loading.value = false;
    }
  };

  // 处理文件选择
  const handleFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      const file = target.files[0];
      await validateAndUploadFile(file);
      target.value = ""; // 清空 input 的值
    }
  };

  // 拖拽事件处理
  const handleDrop = async (event: DragEvent) => {
    event.preventDefault();
    isDragging.value = true;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      await validateAndUploadFile(file);
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    isDragging.value = true;
  };

  const handleDragLeave = () => {
    isDragging.value = false;
  };

  // 验证并上传文件
  const validateAndUploadFile = async (file: File) => {
    selectedFile.value = file;

    const validImageTypes = appConfig.imageExt
      .filter((format) => format.ext !== "gif")
      .map((format) => format.mime)


    const supportedExt = appConfig.imageExt
      .filter((format) => format.ext !== "gif")
      .map((format) => format.ext);
    if (!validImageTypes.includes(file.type)) {
      const errorMessage = `Unsupported file type. Only ${supportedExt.join(",")} are allowed.`;
      snackbar.showError(errorMessage);
      utils.reportError('Face文件类型验证失败', 'face type validation error', { 
        fileType: file.type,
        supportedTypes: validImageTypes 
      });
      return;
    }

    const maxFileSize = 20 * 1024 * 1024;
    if (file.size > maxFileSize) {
      const errorMessage = "File size should not exceed 20MB";
      snackbar.showError(errorMessage);
      utils.reportError('Face文件大小验证失败', 'face size validation error', {
        fileType: file.type,
        fileSize: file.size,
        maxSize: maxFileSize
      });
      return;
    }

    await uploadFace();
  };

  // 上传头像
  const uploadFace = async () => {
    if (!selectedFile.value) {
      const errorMessage = "Please select a file first";
      snackbar.showError(errorMessage);
      utils.reportError('Face上传失败', 'no file selected', {
        error: new Error(errorMessage)
      });
      return;
    }

    try {
      globalLoading.startLoading("Uploading");
      const response = await api.uploadFace(selectedFile.value);
      comm.setUserInfo();
      snackbar.showSnackbar({ color: "success", message: "Upload successful" });
      $logInfo("File uploaded successfully", "uploadFace");
      await loadFaces();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Upload failed";
      snackbar.showError(errorMessage);
      $logError(error.response, "uploadFace");
      utils.reportError('Face上传失败', 'upload face error', { 
        response: error.response,
        fileName: selectedFile.value?.name,
        fileSize: selectedFile.value?.size,
        fileType: selectedFile.value?.type,
      });
    } finally {
      globalLoading.stopLoading();
      selectedFile.value = null;
    }
  };

  const moveToTop = async (faceId: string) => {
    try {
      await api.topedFace(faceId);
      const index = faces.value.findIndex((face) => face.face_id === faceId);
      if (index !== -1) {
        const [selectedFace] = faces.value.splice(index, 1);
        faces.value.unshift(selectedFace);
      }
      snackbar.showSnackbar({
        color: "success",
        message: "Face moved to top successfully",
      });
    } catch (error) {
      snackbar.showError(error.response?.data?.message || "Unknown error");
    }
  };

  const removeFace = async (faceId: string) => {
    try {
      globalLoading.startLoading();
      await api.deleteFace(faceId);
      const index = faces.value.findIndex((face) => face.face_id === faceId);
      if (index !== -1) {
        faces.value.splice(index, 1);
      }
      snackbar.showSnackbar({
        color: "success",
        message: "Removed successfully",
      });
      comm.setUserInfo();
    } catch (error) {
      snackbar.showError(error.response?.data?.message || "Unknown error");
    } finally {
      globalLoading.stopLoading();
    }
  };

  // 添加新上传的人脸到列表顶部
  const addFaceToList = (face: Face) => {
    // 添加到列表开头,这样新上传的会显示在最前面
    faces.value.unshift(face);
  };

  return {
    faces,
    isEmpty,
    isDragging,
    loading,
    selectedFile,
    loadFaces,
    handleFileChange,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    uploadFace,
    moveToTop,
    removeFace,
    addFaceToList,
  };
}
